import React, { forwardRef, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  DataGrid,
  type DataGridProps,
  type GridColDef,
  type GridPaginationModel,
  type GridRowClassNameParams,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import ContextMenu, {
  type ContextMenuItem,
  type ContextMenuParams,
} from '../ContextMenu';
import { RowDetailProvider } from './RowDetailContext';
import {
  type DataTableRowReorder,
  RowReorderProvider,
  createReorderColumn,
  getSortableInnerRow,
  useRowReorderSlot,
} from './rowReorder';
import { useDataTableRows } from './useDataTableRows';

export type DataTableV2Column<R extends GridValidRowModel = GridValidRowModel> =
  Omit<GridColDef<R>, 'renderHeader'> & {
    subheaderName?: string;
  };

export type { DataTableRowReorder };

export type DataTableV2Props<R extends GridValidRowModel = GridValidRowModel> =
  {
    rows: R[];
    columns: DataTableV2Column<R>[];
    loading?: boolean;
    getRowId: DataGridProps<R>['getRowId'];
    getRowDetails?: (row: R) => React.ReactNode;
    getRowClassName?: (params: GridRowClassNameParams<R>) => string;
    processRowUpdate?: DataGridProps<R>['processRowUpdate'];
    onProcessRowUpdateError?: DataGridProps<R>['onProcessRowUpdateError'];
    onCellClick?: DataGridProps<R>['onCellClick'];
    onRowClick?: DataGridProps<R>['onRowClick'];
    apiRef?: DataGridProps<R>['apiRef'];
    editMode?: DataGridProps<R>['editMode'];
    rowHeight?: number;
    fillHeight?: boolean;
    hideFooter?: boolean;
    contextMenuItems?: (params: ContextMenuParams) => ContextMenuItem[];
    paginationMode?: 'client' | 'server';
    rowCount?: number;
    paginationModel?: GridPaginationModel;
    onPaginationModelChange?: (model: GridPaginationModel) => void;
    pageSizeOptions?: number[];
    sortingMode?: 'client' | 'server';
    sortModel?: GridSortModel;
    onSortModelChange?: (model: GridSortModel) => void;
    rowReorder?: DataTableRowReorder;
  };

function DataTableV2Inner<R extends GridValidRowModel = GridValidRowModel>(
  props: DataTableV2Props<R>,
  ref: React.Ref<HTMLDivElement>
) {
  const {
    rows,
    loading = false,
    getRowId,
    getRowDetails,
    getRowClassName,
    processRowUpdate,
    onProcessRowUpdateError,
    onCellClick,
    onRowClick,
    apiRef,
    editMode,
    rowHeight = 40,
    fillHeight = false,
    hideFooter,
    contextMenuItems,
    paginationMode,
    rowCount,
    paginationModel,
    onPaginationModelChange,
    pageSizeOptions,
    sortingMode,
    sortModel,
    onSortModelChange,
    rowReorder,
  } = props;

  const reorderEnabled = Boolean(rowReorder);

  const { gridColumns, columnHeaderHeight } = useMemo(() => {
    let hasSubheader = false;
    const mapped = props.columns.map((column) => {
      const { subheaderName, ...rest } = column;
      if (!subheaderName) return rest as GridColDef<R>;
      hasSubheader = true;
      return {
        ...rest,
        renderHeader: () => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" noWrap>
              {column.headerName ?? column.field}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {subheaderName}
            </Typography>
          </Box>
        ),
      } as GridColDef<R>;
    });

    const withReorder = reorderEnabled
      ? [createReorderColumn<R>(), ...mapped]
      : mapped;

    return {
      gridColumns: withReorder,
      columnHeaderHeight: hasSubheader ? 50 : 40,
    };
  }, [props.columns, reorderEnabled]);

  const {
    columns,
    slots: baseSlots,
    handleRowClick,
    hasRowClick,
    detailContext,
    isRowExpanded,
    isRowMenuOpen,
    handleContextMenu,
    closeMenu,
    menuItems,
    menuAnchor,
    isMenuOpen,
  } = useDataTableRows({
    columns: gridColumns,
    getRowDetails,
    onRowClick,
    contextMenuItems,
  });

  const rowIds = useMemo(
    () => (getRowId ? rows.map((row) => String(getRowId(row))) : []),
    [rows, getRowId]
  );

  const innerRow = getSortableInnerRow(
    baseSlots?.row as Parameters<typeof getSortableInnerRow>[0]
  );
  const slots = useRowReorderSlot(
    reorderEnabled,
    baseSlots?.row as Parameters<typeof useRowReorderSlot>[1]
  );

  const resolvedRowClassName = useCallback(
    (params: GridRowClassNameParams<R>) => {
      const baseClass = getRowClassName?.(params) ?? '';
      const expandedClass = isRowExpanded(params.id) ? ' row-expanded' : '';
      const contextMenuClass = isRowMenuOpen(params.id)
        ? ' row-context-menu-open'
        : '';
      return `${baseClass}${expandedClass}${contextMenuClass}`.trim();
    },
    [getRowClassName, isRowExpanded, isRowMenuOpen]
  );

  const grid = (
    <DataGrid<R>
      ref={ref}
      rows={rows}
      columns={columns}
      loading={loading}
      getRowId={getRowId}
      getRowClassName={resolvedRowClassName}
      processRowUpdate={processRowUpdate}
      onProcessRowUpdateError={onProcessRowUpdateError}
      onCellClick={onCellClick}
      onRowClick={handleRowClick}
      apiRef={apiRef}
      editMode={editMode}
      hideFooter={hideFooter}
      paginationMode={paginationMode}
      rowCount={rowCount}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={pageSizeOptions}
      sortingMode={sortingMode}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      sortingOrder={
        sortingMode === 'server' ? ['asc', 'desc', null] : undefined
      }
      disableColumnMenu
      disableColumnSorting={reorderEnabled}
      disableRowSelectionOnClick
      disableVirtualization={Boolean(slots)}
      slots={slots}
      rowHeight={rowHeight}
      columnHeaderHeight={columnHeaderHeight}
      sx={{
        height: '100%',
        '& .MuiDataGrid-main': { flex: 1 },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
          { outline: 'none' },
        '& .MuiDataGrid-filler>div': { borderTop: 'none' },
        '& .MuiDataGrid-row:last-of-type': {
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
      }}
    />
  );

  return (
    <>
      <RowDetailProvider value={detailContext}>
        <Box
          onContextMenu={handleContextMenu}
          sx={{
            width: '100%',
            ...(fillHeight
              ? { flex: 1, minHeight: 0, height: '100%' }
              : { height: '100%' }),
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell > div': {
              display: 'flex',
            },
            '& .MuiDataGrid-row.row-invalid': {
              backgroundColor: '#fef2f2',
            },
            '& .MuiDataGrid-row.row-expanded': {
              bgcolor: 'action.hover',
              boxShadow: (theme) =>
                `inset 3px 0 0 ${theme.palette.primary.main}`,
            },
            '& .MuiDataGrid-row.row-context-menu-open': {
              backgroundColor: 'action.selected',
            },
            ...(hasRowClick
              ? { '& .MuiDataGrid-row': { cursor: 'pointer' } }
              : {}),
          }}
        >
          {rowReorder ? (
            <RowReorderProvider
              rowIds={rowIds}
              onReorder={rowReorder.onReorder}
              innerRow={innerRow}
            >
              {grid}
            </RowReorderProvider>
          ) : (
            grid
          )}
        </Box>
      </RowDetailProvider>
      {isMenuOpen && menuAnchor ? (
        <ContextMenu
          open
          onClose={closeMenu}
          anchorReference={menuAnchor.anchorReference}
          anchorEl={menuAnchor.anchorEl}
          anchorPosition={menuAnchor.anchorPosition}
          items={menuItems}
        />
      ) : null}
    </>
  );
}

const DataTableV2 = forwardRef(DataTableV2Inner) as <
  R extends GridValidRowModel = GridValidRowModel,
>(
  props: DataTableV2Props<R> & { ref?: React.Ref<HTMLDivElement> }
) => React.JSX.Element;

export default DataTableV2;
